/**
 * SupportPage — the cross-lab helpdesk SCREEN: a ticket triage inbox on the left, an integrated CHAT
 * thread on the right. This fills the shell's support stub (the section the management shell explicitly
 * left for the support agent) — it does NOT touch the shell's shared files (api.ts/types.ts/messages.ts/
 * lib.ts/index.tsx), reusing them and adding its own support.* siblings.
 *
 * Near-real-time: the open chat reads via `useSupportTicket(..., live=true)`, which polls every
 * SUPPORT_LIVE_POLL_MS, so a lab reply lands in the thread within a few seconds with no websocket. A send
 * / mark-read invalidates the management root, refreshing the inbox unread badges + the cross-lab unread
 * KPI in lockstep.
 *
 * Gating mirrors the shell's other sections: the route already rides <PlatformAdminGuard>; this screen
 * additionally renders its own ForbiddenState defensively and short-circuits offline + the cloud-only
 * "module not mounted on this install" state (api.isManagementAbsent). Full 5-state DoD per data source.
 */
import { useState } from "react";
import { Card, ForbiddenState, OfflineState } from "@/ui";
import { useT, useUiStore } from "@/app";
import { ALL_MANAGEMENT_CAPS } from "../caps";
import { isManagementAbsent, useSupportTickets } from "../api";
import { useIsPlatformAdmin } from "../session";
import { registerSupportMessages } from "../support.messages";
import { registerSupportWorkflowMessages } from "../supportWorkflow.messages";
import { PageHeader } from "../components/PageHeader";
import { Screen } from "../components/Screen";
import { SupportInsight } from "../components/SupportInsight";
import { SupportSlaStrip } from "../components/SupportSlaStrip";
import { SupportTicketList } from "../components/SupportTicketList";
import { SupportChatPanel } from "../components/SupportChatPanel";
import type { SupportFilters } from "../types";

// Register the support i18n namespaces at module load (idempotent — safe whether or not onRegister ran).
registerSupportMessages();
registerSupportWorkflowMessages();

export function SupportPage() {
  const { t } = useT();
  const online = useUiStore((s) => s.online);
  const isAdmin = useIsPlatformAdmin();
  const enabled = isAdmin && online;

  const [filters, setFilters] = useState<SupportFilters>({ page: 1, page_size: 20 });
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // A probe read so the screen can surface the offline / cloud-absent states before the panes mount.
  const probe = useSupportTickets({ page: 1, page_size: 1 }, enabled);

  const header = <PageHeader title={t("mgmt.support.title")} subtitle={t("mgmt.support.subtitle")} />;

  if (!isAdmin) {
    return (
      <ForbiddenState
        requiredCapabilities={ALL_MANAGEMENT_CAPS}
        message="Cette console est réservée aux administrateurs de la plateforme Pharmakon."
      />
    );
  }

  if (!online) {
    return (
      <Screen>
        {header}
        <OfflineState onRetry={() => void probe.refetch()} />
      </Screen>
    );
  }

  // Cloud-only module on a local box → its routes 404. Surface the clean "not available here" notice.
  if (isManagementAbsent(probe.error)) {
    return (
      <Screen>
        {header}
        <Card title={t("mgmt.unavailable.title")}>
          <p style={{ margin: 0, color: "var(--nv-text-2, var(--nv-slate))" }}>
            {t("mgmt.unavailable.desc")}
          </p>
        </Card>
      </Screen>
    );
  }

  return (
    <Screen>
      {header}

      <SupportInsight enabled={enabled} />

      {/* SLA-breach indicators — a cross-lab preview; selecting a breached row opens it in the chat/triage. */}
      <SupportSlaStrip enabled={enabled} onSelect={setSelectedId} />

      <div
        style={{
          display: "grid",
          gap: "var(--nv-s-4)",
          gridTemplateColumns: "minmax(0, 1.4fr) minmax(0, 1fr)",
          alignItems: "start",
        }}
        className="nv-support-grid"
      >
        <SupportTicketList
          filters={filters}
          onFiltersChange={setFilters}
          selectedId={selectedId}
          onSelect={setSelectedId}
          enabled={enabled}
        />

        <Card title={t("mgmt.support.chat.title")}>
          <SupportChatPanel ticketId={selectedId} enabled={enabled} />
        </Card>
      </div>
    </Screen>
  );
}
